import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useLingui } from '@lingui/react/macro';

import { theme } from '../styles/GlobalStyle';
import { useWindowWidth } from '../../hooks/useWindowSize';

import { TopOfFold } from '../page-components-copy/top-of-fold';
import { ColorBanner } from '../page-components-copy/color-banner';

import { Spacer } from '../page-components-copy/components';
import { IconFocusCTA } from '../page-components-copy/icon-focus-cta';
import PartnerLanding, { PARTNER } from '../Better/landing';
import { ROUTE_PATHS } from '../_pages/_utils/router.utils';
import { useAuthContext } from '../../providers/auth.provider';
import { IMAGES, PLAN_PRICE, getCampaignFromParams, getImage } from '../../utils';
import getStorage from '../../utils/storage.utils';
import { API_URL } from '../../env';
import { useLocationContext } from '../../providers/location.provider';

const person1 = getImage(IMAGES.PERSON_1.ref);
const Magnify = getImage(IMAGES.MAGNIFY.ref);
const BalloonDog = getImage(IMAGES.BALLOON_DOG.ref);

const Image = styled('img')`
  display: flex;
  z-index: -1;
  margin-top: 24px;
  width: 210px;
  margin: auto;
  @media ${theme.mq.desktopLargeMin} {
    width: 325px;
  }
`;

const Home: React.FC<{ className?: string }> = ({ className }) => {
  const storage = getStorage();
  const { handleSignUp } = useAuthContext();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { t } = useLingui();
  const params = new URLSearchParams(search);
  let campaign: 'credit-score' | 'consolidation' | 'save' | 'ham' | 'cash' | string =
    getCampaignFromParams(params) || '';
  const { isMobile } = useWindowWidth();
  const [source, setSource] = useState<string | null>(params.get('utm_source') || params.get('utm_content'));
  const { isPuertoRico: isPR } = useLocationContext();

  if (window.location.pathname === '/cash') {
    campaign = 'cash';
    storage.setItem('utm_content', 'cash');
  } else if (isPR && !campaign) {
    // PR users hitting the base URL default to the /cash page content (with its
    // PR edits), but WITHOUT the loan-interest side effect (storing utm_content)
    // — that assumption only applies when they explicitly navigate to /cash.
    campaign = 'cash';
  }

  if (source === 'renofi' && API_URL.includes('staging')) {
    window.location.href = 'https://app.solve.finance?utm_source=renofi';
  }

  const partner = PARTNER[source];
  if (partner) return <PartnerLanding partner={partner} campaign={campaign} />;

  const getCampaignCopy = (
    campaign: string,
  ): {
    h1Copy: string | React.ReactNode;
    h2Copy?: string | React.ReactNode;
    pCopy: string | React.ReactNode;
    additionalCopy?: string | React.ReactNode;
    cta?: string;
  } => {
    if (campaign === 'consolidation') {
      return {
        h1Copy: isPR ? t`Lower Your Payments in Minutes` : t`Lower Your Payments and Save in Minutes`,
        pCopy: isPR
          ? t`Refinance and consolidation for any credit - instantly apply across thousands of lenders at touch of a button (with no impact on your score). Then continue to monitor your credit and borrowing options. Destress your financial life.`
          : t`Refinance and consolidation for any credit - instantly apply across thousands of lenders at touch of a button (with no impact on your score). Then continue to monitor your credit and savings opportunities. Destress your financial life - we’re free if we can’t help.`,
      };
    } else if (campaign === 'credit-score') {
      return {
        h1Copy: isPR ? t`Understand Your Credit Score` : t`Increase Your Score Or Your Money Back.`,
        pCopy: isPR
          ? t`Want to understand your score and explore new lower cost loans? Wondering what’s in your credit score? The app brings your score and debt in one place, with tools to help you spot savings, review your report and correct mistakes. Then track and monitor your credit over time. Without spam. Only $${PLAN_PRICE}/month.`
          : t`Want to raise your score and qualify for new lower cost loans? Wondering what’s in your credit score? We work on your score and lowering debt burdens all in one place. We scan for savings, identify mistakes on your report and help you correct. Then we track and continuously monitor for you. Without spam. Only $${PLAN_PRICE}/month and a “Results or Your Money-Back” Guarantee - if we don’t help, we don’t get paid.`,
      };
    } else if (campaign === 'save') {
      return {
        h1Copy: isPR ? t`Lower Your Monthly Debt Payments` : t`Save Money and Lower Your Monthly Debt Payments`,
        h2Copy: isPR
          ? t`Effortlessly navigate to lower cost borrowing.`
          : t`Effortlessly navigate to lower cost borrowing. We’ve identified over $1,000/year savings on average.`,
        pCopy: isPR
          ? t`Quickly scan your credit report against 1000s of lenders for lower cost options. $${PLAN_PRICE} a month. In addition to lowering your monthly or total cost of debt, the app also has tools to help you monitor and improve your credit score, all in one debt dashboard. Ask us any borrowing question you have.`
          : t`Quickly scan your credit report against 1000s of lenders for savings. $${PLAN_PRICE} a month, risk-free – if we don't find ways to save, get your money back. In addition to lowering your monthly or total cost of debt, we also help monitor and improve your credit score, all in one debt dashboard. Ask us any borrowing question you have.`,
      };
    } else if (campaign === 'ham') {
      document.title = isPR
        ? t`Navigate Your Finances | Solve Finance`
        : t`We Optimize Your Home-Buying Power | Solve Finance`;

      return {
        h1Copy: isPR ? t`Navigate Your Borrowing Options` : t`Expert Help For Your Home-Buying Power`,
        h2Copy: isPR
          ? t`Effortlessly navigate your finances. The app gives you tools to understand and improve your finances.`
          : t`Effortlessly navigate to home-buying readiness. We help get you to your dream by working on your homebuying power and finances for you.`,
        pCopy: isPR
          ? t`Quickly scan your credit report against 1000s of lenders for lower cost options. $${PLAN_PRICE} a month. In addition to lowering your monthly or total cost of debt, the app also has tools to help you monitor and improve your credit score, all in one debt dashboard. Ask us any borrowing question you have.`
          : t`Quickly scan your credit report against 1000s of lenders for savings. $${PLAN_PRICE} a month, risk-free – if we don't find ways to save, get your money back. In addition to lowering your monthly or total cost of debt, we also help monitor and improve your credit score, all in one debt dashboard. Ask us any borrowing question you have.`,
        cta: isPR ? t`Get Started` : t`Increase My Homebuying Power`,
      };
    } else if (campaign === 'cash') {
      return {
        h1Copy: (
          <Trans>
            <b>We Find Loans </b>
            <br />
            <span>For Every Situation</span>
          </Trans>
        ),
        h2Copy: t`Get a Solve Finance Personal Loan Today`,
        pCopy: (
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', gap: '8px' }}>
            <li>
              <Trans>Ideal for good or better credit scores</Trans>
            </li>
            <li>
              <Trans>We use bank data to verify income. No impact on credit to apply</Trans>
            </li>
            <li>{isPR ? <Trans>$500 to $5,000 loan amounts</Trans> : <Trans>$500 to $12,500 loan amounts</Trans>}</li>
            <li>
              <Trans>Apply online in 3 minutes. Cash from your couch!</Trans>
            </li>
          </ul>
        ),
        additionalCopy: t`Check out your options. Whether it's lowering payments on credit cards or a surprise home expense, we're here to make it easy for you.`,
      };
    } else {
      return {
        h1Copy: (
          <Trans>
            <b>Our Debt Optimizer Makes</b>
            <br />
            <span>Better Borrowing Easy</span>
          </Trans>
        ),
        h2Copy: isPR
          ? t`Borrow While Improving Your Finances With Our App As Your Co-Pilot`
          : t`Easily Improve Your Finances With Our Borrowing App As Your Co-Pilot`,
        pCopy: isPR
          ? t`Get a loan and effortlessly navigate credit. We try to make loans for everyone, and even if we can't lend to you today, the app can help you track your credit to borrow from us or other lenders in future.`
          : t`Effortlessly navigate credit. Whatever your goal is, we're here to help. We get more borrowers into homes and loans, increase home-buying power > 25% and find savings of over $1,000 a year on average.`,
      };
    }
  };

  const copy = getCampaignCopy(campaign);

  const onSignUp = async () => {
    handleSignUp();
    navigate(ROUTE_PATHS.ONBOARDING);
  };

  useEffect(() => {
    const hydrate = async () => {
      const storedSource = await storage.getItem('solve_source');
      if (!storedSource) {
        setSource(storedSource);
      }
    };

    hydrate();
  }, [source]);

  const ctas = [
    {
      bgColor: theme.green4,
      callback: onSignUp,
      cta: copy.cta || t`Get Started`,
      testid: 'saving-button',
    },
  ];

  if (isMobile) {
    ctas.push({
      bgColor: theme.green4,
      callback: async () => navigate(ROUTE_PATHS.LOGIN),
      cta: t`Sign In`,
      testid: 'sign-in-button',
    });
  }

  return (
    <div className={className}>
      <TopOfFold
        alt={t`A magnifying glass searching for better borrowing options`}
        copy={copy}
        copyWidth="100%"
        ctas={ctas}
        ImageOverride={
          isMobile ? (
            <Image
              src={Magnify}
              style={{
                transform: 'rotate(180)',
                position: 'absolute',
                right: '-64px',
                top: '-48px',
                opacity: 0.25,
              }}
            />
          ) : (
            <Image src={Magnify} />
          )
        }
        hasMobileDownload={campaign === 'ham'}
        continueOnWeb={onSignUp}
        imageLocation={{ desktop: 'left', mobile: 'top' }}
      />
      {/* Gap so the hero's CTAs / closing line don't butt against the orange banner.
          Mobile's stacked full-width buttons need a little more breathing room than desktop. */}
      <Spacer />
      {isMobile && <Spacer />}
      <ColorBanner
        bannerColor="#F2994A"
        copy={{
          headerCopy: <Trans>Quick Options for Everyone</Trans>,
          pCopy: (
            <Trans>
              Your debts cost you money. That’s the interest you pay across credit cards, personal, home, auto, and
              student loans—and as you know, it can add up fast. And if your credit score isn’t where it could be or you
              were just denied a loan, other lenders might overlook you. We try to make a home for everyone, give you
              visibility into your credit report, and after we make a loan try to graduate you to a lower cost loan to
              help make successful finances.
            </Trans>
          ),
        }}
        imageMeta={{
          src: person1,
          mobilePosition: 'bottom',
          alt: isPR
            ? t`A borrower living her stress-free life after using Solve Finance`
            : t`A borrower living her stress-free life after Solve Finance fixes credit for her`,
        }}
      />
      {/* Clearance for the closing CTA. The banner already reserves the portrait's
          overflow on mobile (padding-bottom), so two Spacers is enough on both breakpoints. */}
      <Spacer />
      <Spacer />
      <IconFocusCTA
        icon={<img src={BalloonDog} alt={t`balloon animal dog`} />}
        title={{
          text: t`We make borrowing easy`,
          size: 'l',
        }}
        ctas={[
          {
            text: t`Get Started`,
            callback: onSignUp,
            bgColor: isMobile ? '#EB5757' : theme.blue4,
            color: theme.white,
          },
        ]}
      />
      <Spacer />
    </div>
  );
};

export const HomeStyled = styled(Home)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;
  max-width: 1200px;
  box-sizing: border-box;
  overflow-x: visible;
  padding: 0 0 16px 0;

  @media (max-width: 1079px) {
    margin-top: 30px;
  }
`;

export default HomeStyled;
